import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { AddWalletComponent } from '@feature/dashboard/wallets/components/add-wallet/add-wallet.component';
import {
  updateSenderWalletListFilter,
  resetSenderWalletList,
} from '@feature/dashboard/wallets/store/sender-wallet-list/sender-wallet-list.actions';
import { selectSenderWalletList } from '@feature/dashboard/wallets/store/sender-wallet-list/sender-wallet-list.selector';
import { WalletListInterface } from '@feature/dashboard/wallets/types/wallet-list';
import { WalletListFilter } from '@feature/dashboard/wallets/types/wallet-list-filter';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-sender-wallet-list',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
  ],
  templateUrl: './sender-wallet-list.component.html',
  styleUrl: './sender-wallet-list.component.scss',
})
export class SenderWalletListComponent implements OnInit, OnDestroy {
  @Input() senderId!: string;
  public readonly attrConstant = ATTRIBUTES_CONSTANT.wallets;
  filterForm!: FormGroup;
  displayedColumns: string[] = ['country', 'currency', 'balance', 'created_at'];
  walletList?: WalletListInterface[];
  dataSource!: MatTableDataSource<WalletListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private dialogRef: MatDialog,
    private excelExportService: ExcelExportService
  ) {}

  ngOnInit(): void {
    this.loadData();
    this.dispatchFilter();
  }

  loadData(): void {
    this.store
      .select(selectSenderWalletList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.walletList = data.rawData;
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  addWallet(): void {
    const dialogRef = this.dialogRef.open(AddWalletComponent, {
      disableClose: true,
      data: { senderList: this.senderId },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.dispatchFilter();
    });
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(walletListFilter?: WalletListFilter): void {
    this.store.dispatch(
      updateSenderWalletListFilter({
        filter: { ...walletListFilter, sender_id: [JSON.parse(this.senderId)] },
      })
    );
  }

  export(): void {
    this.excelExportService.exportToExcel(this.walletList!, 'wallets');
  }

  ngOnDestroy(): void {
    this.store.dispatch(resetSenderWalletList());
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
